import { gridExpandedSortedRowEntriesSelector } from '@mui/x-data-grid-premium';
import type { GridApi, GridRowId } from '@mui/x-data-grid-premium';
import React from 'react';
import type { JournalEntryRow } from '../utils/flatten-journal-entries';

export function useLastLineOfEntryIds(
  apiRef: React.MutableRefObject<GridApi>,
  rows: JournalEntryRow[],
): Set<GridRowId> {
  const [ids, setIds] = React.useState<Set<GridRowId>>(() => new Set());

  const recompute = React.useCallback(() => {
    const sortedRows = gridExpandedSortedRowEntriesSelector(apiRef);
    const nextIds = new Set<GridRowId>();
    for (let i = 0; i < sortedRows.length; i++) {
      const current = sortedRows[i].model as JournalEntryRow;
      const ahead = sortedRows[i + 1]?.model as JournalEntryRow | undefined;
      if (!ahead || ahead.journalEntryKey !== current.journalEntryKey) {
        nextIds.add(sortedRows[i].id);
      }
    }
    setIds(nextIds);
  }, [apiRef]);

  React.useEffect(() => {
    recompute();
    const api = apiRef.current;
    if (!api?.subscribeEvent) {
      return;
    }
    const unsubscribers = [
      api.subscribeEvent('sortedRowsSet', recompute),
      api.subscribeEvent('filteredRowsSet', recompute),
    ];
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [apiRef, recompute, rows]);

  return ids;
}
